/**
 * The ticket list's filter/sort/page state, held in the URL.
 *
 * The query string is the state store on purpose: a filtered list survives a
 * reload, the browser back button steps through filter changes, and an employee
 * can paste "my open incidents" into a chat with IT. Every value is validated
 * on read — a hand-edited URL degrades to the default rather than sending
 * nonsense to the API.
 */

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import type {
  ListTicketsQuery,
  SortOrder,
  TicketBucket,
  TicketKind,
  TicketSortField,
  TicketStatusCode,
} from "../api/contract";
import { TICKET_BUCKETS, TICKET_KINDS, TICKET_STATUS_CODES } from "./ticket-display";

/** Rows per page. Matches the contract's default `limit`. */
export const TICKET_PAGE_SIZE = 25;

const SORT_FIELDS: readonly TicketSortField[] = [
  "CREATED_AT",
  "UPDATED_AT",
  "STATUS",
  "PRIORITY",
  "TICKET_NUMBER",
];

/** Free-text search is capped at 200 chars by the contract. */
const MAX_QUERY_LENGTH = 200;

export interface TicketListParams {
  bucket: TicketBucket;
  status: TicketStatusCode[];
  kind: TicketKind | null;
  q: string;
  sort: TicketSortField;
  order: SortOrder;
  /** 1-based, as shown to the user and expected by `<Pagination>`. */
  page: number;
}

export interface UseTicketListParamsResult {
  params: TicketListParams;
  /** Merged patch. Any change except `page` resets back to page 1. */
  setParams: (patch: Partial<TicketListParams>) => void;
  /** Back to the defaults, clearing the query string. */
  reset: () => void;
  /** True when anything is filtering the list. */
  isFiltered: boolean;
  /** The params translated into the contract's query shape. */
  query: ListTicketsQuery;
}

const DEFAULTS: TicketListParams = {
  bucket: "OPEN",
  status: [],
  kind: null,
  q: "",
  sort: "UPDATED_AT",
  order: "DESC",
  page: 1,
};

function oneOf<T extends string>(
  value: string | null,
  allowed: readonly T[],
  fallback: T | null,
): T | null {
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

export function useTicketListParams(): UseTicketListParamsResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<TicketListParams>(() => {
    const rawPage = Number.parseInt(searchParams.get("page") ?? "", 10);
    const status = searchParams
      .getAll("status")
      .flatMap((entry) => entry.split(","))
      .filter((entry): entry is TicketStatusCode =>
        (TICKET_STATUS_CODES as readonly string[]).includes(entry),
      );

    return {
      bucket: oneOf(searchParams.get("bucket"), TICKET_BUCKETS, DEFAULTS.bucket) ?? DEFAULTS.bucket,
      status,
      kind: oneOf(searchParams.get("kind"), TICKET_KINDS, null),
      q: (searchParams.get("q") ?? "").slice(0, MAX_QUERY_LENGTH),
      sort: oneOf(searchParams.get("sort"), SORT_FIELDS, DEFAULTS.sort) ?? DEFAULTS.sort,
      order:
        oneOf(searchParams.get("order"), ["ASC", "DESC"] as const, DEFAULTS.order) ??
        DEFAULTS.order,
      page: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
    };
  }, [searchParams]);

  const setParams = useCallback(
    (patch: Partial<TicketListParams>) => {
      const next: TicketListParams = {
        ...params,
        ...patch,
        // Any filter or sort change invalidates the current offset — page 7 of
        // the old result set is meaningless against the new one.
        page: patch.page ?? (Object.keys(patch).length > 0 ? 1 : params.page),
      };

      const search = new URLSearchParams();
      if (next.bucket !== DEFAULTS.bucket) {
        search.set("bucket", next.bucket);
      }
      for (const code of next.status) {
        search.append("status", code);
      }
      if (next.kind !== null) {
        search.set("kind", next.kind);
      }
      if (next.q.length > 0) {
        search.set("q", next.q.slice(0, MAX_QUERY_LENGTH));
      }
      if (next.sort !== DEFAULTS.sort) {
        search.set("sort", next.sort);
      }
      if (next.order !== DEFAULTS.order) {
        search.set("order", next.order);
      }
      if (next.page > 1) {
        search.set("page", String(next.page));
      }

      setSearchParams(search, { replace: true });
    },
    [params, setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const query = useMemo<ListTicketsQuery>(
    () => ({
      bucket: params.bucket,
      ...(params.status.length > 0 ? { status: params.status } : {}),
      ...(params.kind === null ? {} : { kind: params.kind }),
      ...(params.q.length > 0 ? { q: params.q } : {}),
      sort: params.sort,
      order: params.order,
      limit: TICKET_PAGE_SIZE,
      offset: (params.page - 1) * TICKET_PAGE_SIZE,
    }),
    [params],
  );

  const isFiltered =
    params.bucket !== DEFAULTS.bucket ||
    params.status.length > 0 ||
    params.kind !== null ||
    params.q.length > 0;

  return { params, setParams, reset, isFiltered, query };
}
